/**
 * Breakfast, lunch or dinner — with when each is served and whether it is
 * still taking orders.
 *
 * A closed meal is shown greyed with the reason ("Orders for lunch closed at
 * 10:30 AM") rather than hidden: a customer who came for lunch should learn
 * why it is not there, not wonder whether the kitchen serves it at all.
 */

import { memo } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';

import {
  formatWindow,
  type ServiceSlot,
  type ServiceWindow,
  type SlotAvailability,
} from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Glyph } from '../common/Icon';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

const NO_OUTLINE = { outlineStyle: 'none' } as unknown as ViewStyle;

const GLYPH: Record<ServiceSlot, string> = {
  BREAKFAST: 'breakfast',
  LUNCH: 'lunch',
  DINNER: 'dinner',
};

export interface SlotPickerProps {
  windows: ServiceWindow[];
  /** Whether each meal is open on the chosen date; absent while loading. */
  availability: SlotAvailability[] | undefined;
  value: ServiceSlot | null;
  onChange: (slot: ServiceSlot) => void;
}

export const SlotPicker = memo(function SlotPicker({
  windows,
  availability,
  value,
  onChange,
}: SlotPickerProps) {
  const t = useT();
  const selectedReason = availability?.find((slot) => slot.slot === value)?.reason;

  return (
    <View>
      <View style={styles.row} accessibilityRole="radiogroup" testID="slot-picker">
        {windows
          .filter((window) => window.enabled)
          .map((window) => {
            const status = availability?.find((slot) => slot.slot === window.slot);
            const open = status?.open ?? false;
            const selected = value === window.slot;
            return (
              <Pressable
                key={window.slot}
                testID={`slot-${window.slot}`}
                onPress={() => onChange(window.slot)}
                disabled={!open}
                accessibilityRole="radio"
                accessibilityState={{ selected, disabled: !open }}
                accessibilityLabel={`${t(window.label)}, ${formatWindow(window.start, window.end)}${
                  status?.reason ? `. ${t(status.reason)}` : ''
                }`}
                style={[
                  styles.option,
                  selected && styles.selected,
                  !open && styles.closed,
                  NO_OUTLINE,
                ]}
              >
                <Glyph name={GLYPH[window.slot]} size={22} />
                <Text variant="cardTitle" tone={open ? 'primary' : 'disabled'} align="center">
                  {t(window.label)}
                </Text>
                <Text
                  variant="caption"
                  tone={open ? 'secondary' : 'disabled'}
                  align="center"
                  numberOfLines={2}
                >
                  {formatWindow(window.start, window.end)}
                </Text>
                {!open && status ? (
                  <Text variant="caption" tone="tertiary" align="center" numberOfLines={2}>
                    {t('Closed')}
                  </Text>
                ) : null}
              </Pressable>
            );
          })}
      </View>
      {selectedReason ? (
        <Text variant="caption" tone="danger" style={styles.reason}>
          {t(selectedReason)}
        </Text>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: space.sm,
  },
  option: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    gap: space.xxs,
    paddingVertical: space.md,
    paddingHorizontal: space.xs,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: colors.bg.surface,
  },
  selected: {
    borderWidth: 2,
    borderColor: colors.border.brand,
    backgroundColor: colors.bg.selected,
  },
  closed: {
    backgroundColor: colors.bg.surfaceSunken,
    borderColor: colors.border.subtle,
  },
  reason: {
    marginTop: space.sm,
  },
});
